/**
 * useChangePassword Hook
 * 
 * Handles password change functionality.
 * Encapsulates API calls per Rule 6.
 */

import { useState, useCallback } from 'react';
import { authApi } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';

interface UseChangePasswordReturn {
  isLoading: boolean;
  error: string | null;
  success: boolean;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  clearStatus: () => void;
}

export function useChangePassword(): UseChangePasswordReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const changePassword = useCallback(async (
    currentPassword: string, 
    newPassword: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await authApi.changePassword({ currentPassword, newPassword });
      setSuccess(true);
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to change password';
      logger.error('Password change failed', 'security', String(err));
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearStatus = useCallback(() => {
    setError(null);
    setSuccess(false);
  }, []);

  return {
    isLoading,
    error,
    success,
    changePassword,
    clearStatus,
  };
}

